import * as React from 'react'
import { ComponentProps, JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetProvidersRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Provider } from '@/hooks/useApi/types'

export type ProviderFilterProps = {
    name: string
    onChange: (provider: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    params: Partial<GetProvidersRequestParams>
} & Pick<FilterProps, 'containerRef'>

export const ProviderFilter: React.FC<ProviderFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props

    const { useProvidersQuery } = useFiltersApi()

    const query = useProvidersQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query

    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, Provider>>(() => {
        if (!data) return new Map<string, Provider>()
        return new Map(
            data.map((provider) => [provider.providerId.toString(), provider])
        )
    }, [data])

    const options = data?.map((provider) => {
        return {
            value: provider.providerId.toString(),
            label: provider.providerName,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                return (
                    <div>
                        {highlightMatchingText?.(rawOption.providerName) ??
                            rawOption.providerName}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.provider.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.provider.label`),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
    }

    return <SelectFilter {...passedProps} />
}
